/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PermissionField.tsx
 🗓️ Created: 2025-01-06
 🧩 Description:
    Permission-aware field wrapper component that automatically handles
    disabled state and styling based on user permissions.

 🔧 Notes:
    - Integrates with useFormPermissions for permission checking
    - Provides consistent UI for permission-based fields
    - Supports different field types and sensitivity levels
    - Implements disabled state and visual feedback

 🔁 Last Updated: 2025-01-06 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { ReactNode } from 'react';
import { useFormPermissions } from '../../hooks/useFormPermissions';

interface PermissionFieldProps {
  moduleId: string | number;
  fieldType?: 'readonly' | 'editable' | 'sensitive';
  children: ReactNode;
  className?: string;
  showTooltip?: boolean;
  fallbackContent?: ReactNode;
}

export default function PermissionField({
  moduleId,
  fieldType = 'editable',
  children,
  className = '',
  showTooltip = true,
  fallbackContent
}: PermissionFieldProps) {
  const { getFieldProps, getFieldPermission, getPermissionMessage } = useFormPermissions(moduleId);

  const hasPermission = getFieldPermission(fieldType);
  const fieldProps = getFieldProps(fieldType);

  // If no permission and fallback content is provided, show fallback
  if (!hasPermission && fallbackContent) {
    return (
      <div
        className={`opacity-50 cursor-not-allowed ${className}`}
        title={showTooltip ? getPermissionMessage(fieldType) : undefined}
      >
        {fallbackContent}
      </div>
    );
  }

  // If no permission and no fallback, don't render
  if (!hasPermission && !fallbackContent) {
    return null;
  }

  // Clone children and apply permission props
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        ...fieldProps,
        ...(child.props as Record<string, unknown>)
      });
    }
    return child;
  });

  return (
    <div
      className={`${fieldProps.className} ${className}`}
      title={showTooltip ? fieldProps.title : undefined}
    >
      {childrenWithProps}
    </div>
  );
}

// Convenience components for common field types
export const ReadonlyField = ({ moduleId, children, ...props }: Omit<PermissionFieldProps, 'fieldType'>) => (
  <PermissionField moduleId={moduleId} fieldType="readonly" {...props}>
    {children}
  </PermissionField>
);

export const EditableField = ({ moduleId, children, ...props }: Omit<PermissionFieldProps, 'fieldType'>) => (
  <PermissionField moduleId={moduleId} fieldType="editable" {...props}>
    {children}
  </PermissionField>
);

export const SensitiveField = ({ moduleId, children, ...props }: Omit<PermissionFieldProps, 'fieldType'>) => (
  <PermissionField moduleId={moduleId} fieldType="sensitive" {...props}>
    {children}
  </PermissionField>
);
